/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

/** Utilities */
import formatDate from "../../lib/formatDate";
import { decryptUrlData } from "../../services/CryptoService";
import { normalizeTaskGridRows } from "../../components/tasks/adapters/normalizeTaskGridRow";

/** Common Components */
import TaskCard from "../../components/board/TaskCard";
import TaskBoard from "../../components/board/TaskBoard";
import ViewToggle from "../../components/ui/viewToggle/ViewToggle";
import NoDataFound from "../../components/ui/noDataFound/NoDataFound";
import DropDown from "../../components/finalForms/ui/inputs/dropdown/DropDown";
import Buttons from "../../components/ui/button/Buttons";
import SidebarModel from "../../components/ui/sidebarModel/SidebarModel";
import Toolbar from "../../components/tasklayout/toolbar/Toolbar";
import Viewbar from "../../components/tasklayout/toolbar/features/viewbar/Viewbar";
import CreateTask from "../task/CreateTask";

/** Common Services & Data Files */
import { groupByOptions } from "./Task.data";
import { getCardInfo } from "../../lib/getCardInfo";
import { TaskPageEnums } from "../../utils/Enums/TaskPageEnums";
import { useTaskFilter } from "../../utils/Context/TaskFilterContext";

/** RTK Query */
import { useLazyGetAllTaskStatusQuery } from "../../app/services/taskStatusAPI";
import { useGetPlanningListByUserIdAndProjectIdMutation } from "../../app/services/teamPlanningAPI";
import { useGetTaskListForPlanningByProjectIdMutation, useUpdateTaskMutation } from "../../app/services/taskAPI";

/** CSS Files */
import "./TaskBoard.scss";

const ProjectTaskBoard = () => {
  let projectId = null;
  let projectName = "";
  let projectCode = "";

  const { id } = useParams();
  const projectdata = id ? decryptUrlData(id) : null;
  const { user } = useSelector((state) => state.auth);
  const { filterRequest, resetFilters } = useTaskFilter();

  if (typeof projectdata === "object" && projectdata !== null) {
    projectId = projectdata.projectId;
    projectName = projectdata.projectName;
    projectCode = projectdata.projectCode;
  } else if (projectdata) {
    projectId = projectdata;
  }

  const [viewType, setViewType] = useState("list");
  const [taskList, setTaskList] = useState([]);
  const [taskStatus, setTaskStatus] = useState([]);
  const [planningId, setplanningId] = useState(null);
  const [planningList, setplanningList] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [updateTask, {
    isLoading: updateTaskLoading,
    isSuccess: isSuccessUpdateTask,
    data: updateTaskTData,
  }] = useUpdateTaskMutation();

  const [getTaskListForPlanningByProjectId, {
    isFetching: isgetTaskListForPlanningByProjectIdFetching,
    isSuccess: isgetTaskListForPlanningByProjectIdSuccess,
    data: isgetTaskListForPlanningByProjectId,
  }] = useGetTaskListForPlanningByProjectIdMutation();

  const [getPlanningListByUserIdAndProjectId, {
    isFetching: isgetTeamPlanningListByUserIdfetching,
    isSuccess: isgetTeamPlanningListByUserIdsuccess,
    data: isgetTeamPlanningListByUserIdData,
  }] = useGetPlanningListByUserIdAndProjectIdMutation();

  const [getAllTaskStatus, {
    isFetching: isTaskStatusfetching,
    isSuccess: isTaskStatussuccess,
    data: isTaskStatusData,
  }] = useLazyGetAllTaskStatusQuery();

  const showListView = () => setViewType("list");
  const showCardView = () => setViewType("card");

  const getPlanningIdValue = (value = planningId) =>
    typeof value === "object" ? value?.value : value;

  const fetchPlanningTasks = (nextPlanningId = planningId, activeFilters = filterRequest) => {
    const planningIdValue = getPlanningIdValue(nextPlanningId);
    if (!projectId || !user?.userId || planningIdValue == null) return;

    getTaskListForPlanningByProjectId({
      projectId,
      userId: user.userId,
      planningId: planningIdValue,
      filterData: JSON.stringify(activeFilters || []),
    });
  };

  useEffect(() => {
    if (
      !isgetTaskListForPlanningByProjectIdFetching &&
      isgetTaskListForPlanningByProjectIdSuccess &&
      isgetTaskListForPlanningByProjectId
    ) {
      setHasLoadedOnce(true);
      setTaskList(normalizeTaskGridRows(isgetTaskListForPlanningByProjectId));
    }
  }, [
    isgetTaskListForPlanningByProjectIdFetching,
    isgetTaskListForPlanningByProjectIdSuccess,
    isgetTaskListForPlanningByProjectId,
  ]);

  useEffect(() => {
    if (!isTaskStatusfetching && isTaskStatussuccess && isTaskStatusData) {
      const status = isTaskStatusData.map((item) => ({
        value: item.taskStatusId,
        label: item.statusName,
        className: item.isComplete ? "green-text" : "blue-text",
      }));
      setTaskStatus(status);
    }
  }, [isTaskStatusfetching, isTaskStatussuccess, isTaskStatusData]);

  const onItemMove = (task, newGroup) => {
    updateTask({
      taskStatusId: newGroup,
      taskId: task.taskId,
    });
  };

  const patchTask = (taskId, patch) => {
    setTaskList((prev) =>
      prev.map((task) => (task.taskId === taskId ? { ...task, ...patch } : task))
    );
  };

  useEffect(() => {
    if (isSuccessUpdateTask && updateTaskTData) {
      if (updateTaskTData.errorMessage?.includes("exists")) {
        return;
      }
      fetchPlanningTasks();
    }
  }, [isSuccessUpdateTask, updateTaskTData]);

  useEffect(() => {
    if (
      isgetTeamPlanningListByUserIdsuccess &&
      !isgetTeamPlanningListByUserIdfetching &&
      isgetTeamPlanningListByUserIdData
    ) {
      const currentDate = new Date();

      const planningListData = isgetTeamPlanningListByUserIdData?.map((item) => ({
        value: item.teamPlanningId,
        label: `${formatDate(item.startDate, "DD/MM/yyyy")} - ${formatDate(item.endDate, "DD/MM/yyyy")}`,
      }));
      setplanningList(planningListData);

      const currentPlanning = isgetTeamPlanningListByUserIdData.find((item) => {
        const startDate = new Date(item.startDate);
        const endDate = new Date(item.endDate);

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        return currentDate >= startDate && currentDate <= endDate;
      });

      if (currentPlanning) {
        setplanningId(currentPlanning.teamPlanningId);
        fetchPlanningTasks(currentPlanning.teamPlanningId);
      }
    }
  }, [
    isgetTeamPlanningListByUserIdsuccess,
    isgetTeamPlanningListByUserIdfetching,
    isgetTeamPlanningListByUserIdData,
  ]);

  const handleRefreshGrid = () => {
    fetchPlanningTasks();
  };

  const handleDropdownChange = (selectedOption) => {
    setplanningId(selectedOption);
    fetchPlanningTasks(selectedOption);
  };

  useEffect(() => {
    getAllTaskStatus();
    getPlanningListByUserIdAndProjectId({
      projectId,
      userId: user.userId,
    });
  }, []);

  useEffect(() => {
    fetchPlanningTasks(planningId, filterRequest);
  }, [filterRequest]);

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    handleRefreshGrid();
  };

  const filteredTaskList = searchText
    ? taskList.filter((task) => {
        const needle = searchText.toLowerCase();
        return (
          String(task.taskId ?? "").toLowerCase().includes(needle) ||
          String(task.title ?? "").toLowerCase().includes(needle)
        );
      })
    : taskList;

  const isInitialLoad =
    isgetTaskListForPlanningByProjectIdFetching && !hasLoadedOnce;
  const cardData = [{ key: projectCode, value: projectName }];
  const resolvedPlanningId = getPlanningIdValue();

  return (
    <div className="main-task-board-section">
      <div className="taskboard-filter d-flex center-top-space">
        <div className="task-grid-dropdown">
          {cardData ? getCardInfo(cardData) : null}
        </div>
        <div className="d-flex align-items-center">
          <div className="task-grid-dropdown w-100 mr-3">
            <div className="additional-detail-label">
              <DropDown
                title="Planning"
                type="dropdown"
                options={planningList}
                value={planningId}
                placeholder="Select Planning"
                classNamePrefix="report-user-type"
                isSearchable={false}
                onChange={handleDropdownChange}
              />
            </div>
          </div>
          <Buttons
            buttonTypeClassName="theme-btn mr-3"
            onClick={() => setShowCreateModal(true)}
            buttonText="Task"
            textWithIcon={true}
            fontIcon={true}
            iconName="material-symbols:add-rounded"
          />
          <ViewToggle
            onListViewClick={showListView}
            onCardViewClick={showCardView}
            viewType={viewType}
          />
        </div>
      </div>
      <div className="taskboard-body">
        {viewType === "list" ? (
          <div className="task-layout">
            <div>
              <Viewbar
                projectId={projectId}
                showCompleted={false}
                onToggleChange={() => {}}
                onSearchChange={setSearchText}
                taskFilterPageId={TaskPageEnums.PROJECT_PLANNED_TASK}
                allowSearch={true}
              />
            </div>
            <div className="task-layout-toolbar">
              {filteredTaskList.length > 0 || isInitialLoad || hasLoadedOnce ? (
                <Toolbar
                  listOnly
                  projectId={projectId}
                  taskListData={filteredTaskList}
                  isTeamPlanning={false}
                  allowEditStatus={true}
                  allowEditAssignee={true}
                  isShowActualTime={false}
                  isDataLoading={isInitialLoad}
                  isRefreshing={
                    isgetTaskListForPlanningByProjectIdFetching && hasLoadedOnce
                  }
                  isError={false}
                  onClearFilters={resetFilters}
                  hasActiveFilters={(filterRequest || []).length > 0}
                  hasActiveSearch={Boolean(searchText)}
                  onRefreshGrid={handleRefreshGrid}
                  onTaskPatch={patchTask}
                />
              ) : (
                <NoDataFound />
              )}
            </div>
          </div>
        ) : filteredTaskList.length > 0 ? (
          <TaskBoard
            data={filteredTaskList}
            groupByField="statusName"
            columns={taskStatus}
            groupByOptions={groupByOptions}
            renderItem={(item) => <TaskCard task={item} />}
            onItemMove={(movedItem, newGroup) => onItemMove(movedItem, newGroup)}
          />
        ) : (
          <NoDataFound />
        )}
      </div>
      {showCreateModal && (
        <SidebarModel
          isOpen={showCreateModal}
          onClose={handleCloseCreateModal}
          modalTitle="Create Task"
          modalSize="w-50"
          backdropClosable={false}
        >
          <CreateTask
            projectId={projectId}
            typeId={1}
            PlanningId={resolvedPlanningId}
            onClose={handleCloseCreateModal}
          />
        </SidebarModel>
      )}
    </div>
  );
};

export default ProjectTaskBoard;
